import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPut } from "../lib/api.ts";
import { OutlineButton } from "./FilterBar.tsx";
import { theme } from "../theme.ts";
import type { SafetySettings, SafetySettingsResponse } from "@march/shared";

const CATS: Array<{ key: keyof SafetySettings["targets"]; label: string; type: string }> = [
  { key: "Frame", label: "Frames", type: "Frame" },
  { key: "Base", label: "Bases", type: "Base" },
  { key: "Fabric", label: "Fabric", type: "Fabric" },
  { key: "Cover", label: "Sunbrella covers", type: "Cover" },
  { key: "ImportedCover", label: "Olefin & Pacifica covers", type: "ImportedCover" },
  { key: "Trim", label: "Trim", type: "Trim" },
  { key: "ProtectiveCover", label: "Protective covers", type: "ProtectiveCover" },
];
const TARGETS = [0, 0.9, 0.95, 0.98];
const same = (a: SafetySettings, b: SafetySettings) => JSON.stringify(a) === JSON.stringify(b);

/** Safety buffer dials: target per category and what "95%" means. */
export function SafetyCard() {
  const qc = useQueryClient();
  const q = useQuery<SafetySettingsResponse>({
    queryKey: ["safety-settings"],
    queryFn: () => apiGet<SafetySettingsResponse>("/api/settings/safety"),
  });
  const [draft, setDraft] = useState<SafetySettings | null>(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    if (q.data && !draft) setDraft(structuredClone(q.data.settings));
  }, [q.data, draft]);

  if (q.isLoading || !draft) return <div style={{ color: theme.color.textMuted }}>Loading…</div>;
  if (q.error) return <div style={{ color: theme.color.danger }}>{(q.error as Error).message}</div>;
  const d = q.data!;
  const dirty = !same(draft, d.settings);

  const save = async (value: SafetySettings) => {
    setSaving(true);
    setMsg(null);
    try {
      await apiPut("/api/settings/safety", value);
      setMsg({ ok: true, text: "Saved. Buffers are being recalculated — Need to order updates within about a minute." });
      await qc.invalidateQueries({ queryKey: ["safety-settings"] });
      setDraft(null);
      setTimeout(() => qc.invalidateQueries({ queryKey: ["safety-settings"] }), 60_000);
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    } finally {
      setSaving(false);
    }
  };

  const calibFor = (type: string) =>
    d.calibration.filter((c) => c.item_type === type || (type === "Cover" && c.item_type === "CoverBase"));

  return (
    <div style={{ display: "grid", gap: theme.space.lg }}>
      <div style={{ fontSize: theme.font.size.sm, color: theme.color.textMuted, lineHeight: 1.5 }}>
        Extra stock on top of lead-time demand, so a busier-than-forecast stretch doesn't empty the
        shelf before a new order can land. Each SKU's buffer is sized from how wrong its own past
        forecasts were, and recalculated nightly.
      </div>

      <div style={{ display: "grid", gap: 6 }}>
        <div style={{ fontSize: theme.font.size.sm, fontWeight: 600 }}>What the target means</div>
        {(
          [
            ["cycle", "No stockout in that share of lead-time windows", "e.g. 95% = run out about 1 time in 20. Costly: frames need roughly 2× lead-time demand."],
            ["fill", "That share of demand shipped from stock", "e.g. 95% = up to 5% of units wait. Much cheaper: roughly a third of lead-time demand."],
          ] as const
        ).map(([mode, label, help]) => (
          <label key={mode} style={{ display: "flex", gap: 8, alignItems: "baseline", fontSize: theme.font.size.sm, cursor: "pointer" }}>
            <input type="radio" name="safety-mode" checked={draft.mode === mode} onChange={() => setDraft({ ...draft, mode })} />
            <span>
              <b>{label}</b>
              <span style={{ color: theme.color.textFaint }}> — {help}</span>
            </span>
          </label>
        ))}
      </div>

      <table style={{ borderCollapse: "collapse", fontSize: theme.font.size.sm, maxWidth: 640 }}>
        <thead>
          <tr style={{ color: theme.color.textMuted, fontSize: theme.font.size.xs }}>
            <th style={{ textAlign: "left", padding: "6px 8px" }}>Category</th>
            <th style={{ textAlign: "left", padding: "6px 8px" }}>Target</th>
            <th style={{ textAlign: "left", padding: "6px 8px" }}>Current sizing</th>
          </tr>
        </thead>
        <tbody>
          {CATS.map((c) => (
            <tr key={c.key} style={{ borderTop: `1px solid ${theme.color.border}` }}>
              <td style={{ padding: 8 }}>
                <b>{c.label}</b>
              </td>
              <td style={{ padding: 8 }}>
                <select
                  aria-label={`${c.label} safety target`}
                  value={draft.targets[c.key]}
                  onChange={(e) => setDraft({ ...draft, targets: { ...draft.targets, [c.key]: Number(e.target.value) } })}
                  style={{ padding: "4px 6px", border: `1px solid ${theme.color.border}`, borderRadius: theme.radius.sm }}
                >
                  {TARGETS.map((t) => (
                    <option key={t} value={t}>
                      {t === 0 ? "Off" : `${Math.round(t * 100)}%`}
                    </option>
                  ))}
                </select>
              </td>
              <td style={{ padding: 8, fontSize: theme.font.size.xs, color: theme.color.textMuted }}>
                {calibFor(c.type).length === 0
                  ? "No buffer"
                  : calibFor(c.type)
                      .map(
                        (x) =>
                          `forecasts off by ~${Math.round(x.pooled_rel * 100)}% · multiplier ${x.k.toFixed(2)} from ${x.windows.toLocaleString()} past windows`,
                      )
                      .join("; ")}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div style={{ display: "flex", gap: theme.space.sm, alignItems: "center", flexWrap: "wrap" }}>
        <button
          type="button"
          disabled={!dirty || saving}
          onClick={() => save(draft)}
          style={{
            padding: "6px 14px",
            borderRadius: theme.radius.md,
            border: "none",
            background: dirty ? theme.color.brand : theme.color.border,
            color: theme.color.onBrand,
            fontWeight: 600,
            cursor: dirty && !saving ? "pointer" : "default",
          }}
        >
          {saving ? "Saving…" : "Save safety buffer"}
        </button>
        {dirty && <OutlineButton onClick={() => setDraft(structuredClone(d.settings))}>Discard changes</OutlineButton>}
        {!d.is_default && !dirty && (
          <OutlineButton onClick={() => save(d.defaults)} disabled={same(d.settings, d.defaults)}>
            Reset to defaults
          </OutlineButton>
        )}
        <span style={{ fontSize: theme.font.size.xs, color: theme.color.textFaint }}>
          {d.is_default
            ? "Using defaults (Frames and Bases 95%, no stockout in 95% of windows)"
            : `Last changed by ${d.updated_by ?? "unknown"} on ${new Date(d.updated_at!).toLocaleString()}`}
        </span>
      </div>
      {msg && (
        <div
          style={{
            padding: "8px 10px",
            borderRadius: theme.radius.md,
            background: msg.ok ? theme.color.successFaint : theme.color.dangerFaint,
            color: msg.ok ? theme.color.successText : theme.color.dangerText,
            fontSize: theme.font.size.sm,
          }}
        >
          {msg.text}
        </div>
      )}
    </div>
  );
}
